import { Edit2, KeyRound } from "lucide-react";
import { formatPercentBR } from "../../utils/format";
import "./custom-list.scss";

import { FunctionComponent, useEffect, useState } from "react"

interface StatusOption {
    value: string;
    label: string;
}

interface ListHomeAdminProps {
    title?: string;
    subTitle?: string;
    noTitle?: boolean;
    data?: Array<any>;
    options?: boolean;
    editItem?: (id: number) => void; // 👈 callback
    // Quando passado, a coluna 'status' vira um <select> editavel que dispara
    // onStatusChange(id, novoStatus). Opt-in: outras telas seguem com badge.
    onStatusChange?: (id: number, status: string) => void;
    statusOptions?: StatusOption[];
    // Quando passado, adiciona uma coluna com o botao de resetar senha.
    // Opt-in, igual ao onStatusChange: as outras listagens seguem iguais.
    onResetPassword?: (id: number) => void;
}

const DEFAULT_STATUS_OPTIONS: StatusOption[] = [
    { value: 'active',   label: 'Ativo' },
    { value: 'inactive', label: 'Inativo' },
];

// Select de status pre-selecionado com o valor atual. Se o valor do banco nao
// estiver entre as opcoes (ex.: 'progress'/'blocked'), exibe-o assim mesmo para
// nao "trocar" o status silenciosamente ao renderizar.
const StatusSelect: FunctionComponent<{
    value: string;
    options: StatusOption[];
    onChange: (status: string) => void;
}> = ({ value, options, onChange }) => {
    const hasCurrent = options.some((o) => o.value === value);
    return (
        <select
            className="status-select"
            value={value}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onChange(e.target.value)}
        >
            {!hasCurrent && <option value={value}>{value}</option>}
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    );
};

const CustomList : FunctionComponent<ListHomeAdminProps> = ({ title, subTitle, noTitle, data, options, editItem, onStatusChange, statusOptions, onResetPassword }) => {

    // Colunas extras (editar / resetar senha) derivam direto das props — sem
    // estado, pra nao ficar "presa" quando o componente e reaproveitado.
    const aditionalColunms = (options ? 1 : 0) + (onResetPassword ? 1 : 0);
    const[columns,setColumns] = useState<Array<string>>([]);
    // Reset e destrutivo: o primeiro clique arma, o segundo confirma.
    const[confirmingReset, setConfirmingReset] = useState<number|null>(null);

    const verifyField = (field:string, value:string) => {
        if (field === 'status') {
            if (value.toLocaleLowerCase() === 'ativa') return "badge success";
            return "badge warning";
        }
        if (field === 'senha') {
            return value === 'padrao' ? "badge warning" : "badge success";
        }
        return false;
    }

    const hasBadge = (field:string) => {
        if (field === 'status') return true;
        if (field === 'senha') return true;
        return false;
    }

    const getDataColumns = (data:Array<any>) : boolean => {
        if (!data || data.length === 0) return false;
        const el = data[0];
        setColumns(
            Object.keys(el).filter(
                key => key !== 'asset_network' && key !== 'network_id'
            )
        );
        return true;
    }

    useEffect( () => {
        if(data) getDataColumns(data);
    }, [data])

    return (
        <>
            <div className="list-items-wrapper">

                <div className="list-item">

                    <div className="header" style={{ '--cols': columns.length + aditionalColunms }as React.CSSProperties}>
                        {columns.map((column, i) =>
                            column !== 'asset_network' && column !== 'network_id' && (
                                <span key={i}>{column}</span>
                            )
                        )}
                    </div>
                    <div className="items-list">

                    { data && 
                        <>
                            {data.map((obj, index) => (
                                <div className="item" key={index} style={{ display: 'grid', '--cols': columns.length + aditionalColunms }as React.CSSProperties}>
                                    {Object.entries(obj).map(([key, value]) =>
                                    key !== 'asset_network' && key !== 'network_id' && (
                                        <span key={key} data-label={key}>
                                        {key === 'status' && onStatusChange ? (
                                            <StatusSelect
                                                value={String(value)}
                                                options={statusOptions ?? DEFAULT_STATUS_OPTIONS}
                                                onChange={(status) => onStatusChange(Number(obj.id), status)}
                                            />
                                        ) : hasBadge(key) ? (
                                            <div className={`badge ${verifyField(key, String(value))}`}>
                                                {String(value)}
                                            </div>
                                        ) : (
                                            <>
                                                { key === 'createdAt' ? String(value).split(",")[0] : String(value) }
                                            </>
                                        )}
                                        </span>
                                    )
                                    )}
                                    {options && (
                                        <span data-label="acao">
                                            <div className="btn ghost">
                                                <Edit2 size={20} onClick={() => editItem?.(obj.id)}/>
                                            </div>
                                        </span>
                                    )}
                                    {onResetPassword && (
                                        <span data-label="senha">
                                            <button
                                                type="button"
                                                className={`btn-reset-password ${confirmingReset === Number(obj.id) ? 'confirming' : ''}`}
                                                title="Resetar a senha deste usuario para a padrao"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (confirmingReset === Number(obj.id)) {
                                                        setConfirmingReset(null);
                                                        onResetPassword(Number(obj.id));
                                                    } else {
                                                        setConfirmingReset(Number(obj.id));
                                                    }
                                                }}
                                                onBlur={() => setConfirmingReset(null)}
                                            >
                                                <KeyRound size={15} />
                                                {confirmingReset === Number(obj.id) ? 'Confirmar?' : 'Resetar senha'}
                                            </button>
                                        </span>
                                    )}
                                </div>
                            ))}
                        </>
                    }
                    </div>

                </div>
            </div>
        </>
    )
}

export default CustomList;
